import { Task } from '@/shared/types/task';
import { useAuthStore } from '@/store/auth';
import { useTaskFiltersStore } from '@/entities/task/model/stores/task-filters';
import { format, parseISO } from 'date-fns';
import { Loader } from 'lucide-react';
import { useMemo, useState } from 'react';
import { MeetingCardHeader } from '../meetingCardHeader';

interface MeetingCalendarProps {
  isOpen: boolean;
  onCreateMeeting?: () => void;
  tasks: Task[];
  maxHeightPercentage?: number;
  taskType?: 'meeting' | 'task';
  isLoading?: boolean;
}

type LegacyStatusTask = { STATUS?: string | number | { Int64?: number } };

/** Сортируем/группируем по крайнему сроку (дедлайну), план старта — запасной вариант. */
function meetingStartIso(task: Task): string | null {
  return task.dueDate ?? task.startDatePlan ?? null;
}

function meetingStartMs(task: Task): number {
  const iso = meetingStartIso(task);
  const ms = iso ? parseISO(iso).getTime() : NaN;
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

export function MeetingCalendarHeader({
  tasks,
  taskType = 'meeting',
  isLoading,
}: MeetingCalendarProps) {
  const { selectedDate } = useTaskFiltersStore();
  const { userId } = useAuthStore();
  const [expandedGroup] = useState<string | null>(null);

  function isDoneTask(task: Task): boolean {
    const legacyRaw = (task as unknown as LegacyStatusTask).STATUS;
    const legacyVal =
      typeof legacyRaw === 'object' &&
      legacyRaw !== null &&
      'Int64' in legacyRaw
        ? legacyRaw.Int64
        : legacyRaw;

    return task.status === 'done' || String(legacyVal ?? '') === '5';
  }

  const filteredMeetings = useMemo(() => {
    if (taskType === 'task') {
      return tasks.filter((t) => !isDoneTask(t));
    }

    return tasks
      .filter((task) => {
        if (isDoneTask(task)) {
          return false;
        }
        if (!task.dueDate) {
          return false;
        }

        const isOnSelectedDate =
          format(parseISO(task.dueDate), 'yyyy-MM-dd') ===
          format(selectedDate, 'yyyy-MM-dd');

        const accomplices = Array.isArray(task.accomplices)
          ? task.accomplices
          : [];
        const auditors = Array.isArray(task.auditors) ? task.auditors : [];
        const isParticipant =
          task.assigneeId === userId ||
          accomplices.includes(userId as number) ||
          (task as { createdBy?: number | null }).createdBy === userId ||
          auditors.includes(userId as number);

        return isOnSelectedDate && isParticipant;
      })
      .sort((a, b) => meetingStartMs(a) - meetingStartMs(b));
  }, [tasks, selectedDate, userId, taskType]);

  const { tasksWithTime, tasksWithoutTime } = useMemo(() => {
    const withTime: Task[] = [];
    const withoutTime: Task[] = [];

    filteredMeetings.forEach((task) => {
      if (task.dueDate) {
        withTime.push(task);
      } else {
        withoutTime.push(task);
      }
    });

    return { tasksWithTime: withTime, tasksWithoutTime: withoutTime };
  }, [filteredMeetings]);

  const taskGroups = useMemo(() => {
    const grouped = tasksWithTime.reduce(
      (groups, task) => {
        const iso = meetingStartIso(task);
        if (!iso) {
          return groups;
        }
        const taskTime = parseISO(iso).toTimeString().slice(0, 5);
        (groups[taskTime] ||= []).push(task);
        return groups;
      },
      {} as Record<string, Task[]>
    );

    return Object.entries(grouped).map(([time, tasks]) => ({ time, tasks }));
  }, [tasksWithTime]);

  const emptyStateText = {
    main: 'Встреч на эту дату нет',
    secondary: 'или вы не являетесь участником',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center text-white py-3">
        <Loader className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (filteredMeetings.length === 0) {
    return (
      <div className="flex flex-col items-center text-[16px] text-white justify-center py-5">
        <p>{emptyStateText.main}</p>
        <p className="mt-1">{emptyStateText.secondary}</p>
      </div>
    );
  }

  return (
    <div className="py-2">
      {taskGroups.length > 0 && (
        <div className="px-3 flex flex-col gap-[8px]">
          {taskGroups.map((group) => {
            const isGroupExpanded = expandedGroup === group.time;

            if (group.tasks.length === 1) {
              return (
                <MeetingCardHeader
                  variant="light"
                  key={group.tasks[0].id}
                  task={group.tasks[0]}
                />
              );
            }

            if (!isGroupExpanded) {
              return (
                <MeetingCardHeader
                  variant="light"
                  key={`${group.time}-header`}
                  task={group.tasks[0]}
                />
              );
            }

            return group.tasks.map((task) => (
              <MeetingCardHeader variant="light" key={task.id} task={task} />
            ));
          })}
        </div>
      )}

      {tasksWithoutTime.length > 0 && (
        <div className="mt-4">
          {tasksWithoutTime.map((task) => (
            <MeetingCardHeader variant="light" key={task.id} task={task} />
          ))}
        </div>
      )}
    </div>
  );
}
